import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { installDevTenantIdentity } from '@erp/shared'
import App from './App.vue'
import router from './router'
import './styles/global.css'
import './styles/seller-core-ui.css'
// 🔴 货代端的暗色作业主题 —— 已作用域化到 `.fwd-scope`（见文件头注释），全局 import 不污染卖家端。
//   由 ForwarderFrame 在它的根 `<main class="fwd-scope">` 上挂载生效。
import '@/forwarder/styles/forwarder.css'

/**
 * 🔴🔴 开发期租户身份 —— **整个应用里唯一装它的地方**。
 *
 * 生产构建里 `import.meta.env.DEV` 为 false ⇒ 这一段被 Vite 静态删除 ⇒
 * `X-Tenant-Id` 头**根本不会出现在产物里**，请求会走真实登录拿到的令牌。
 *
 * ⚠️ 登录已实现（`src/api/auth.ts`，打真实的 `/auth/v1/tenant/login`，2026-08-06 实测通过）。
 * 这段开发期身份只是**未登录时本地调试**用的兜底——登录守卫见 `router/index.ts`。
 * 生产里登录后的请求带的是令牌里的租户，与此处无关。
 * ⛔ 不要为了「让生产也能跑」把这个判断去掉 —— 那等于让任何人自己指定是哪个租户。
 */
if (import.meta.env.DEV) {
  const raw = import.meta.env.VITE_DEV_TENANT_ID ?? '1001'
  const tenantId = Number(raw)
  installDevTenantIdentity({
    // 1001 = sql/seed/seed_dev.sql 里的「甲跨境（测试租户A）」
    tenantId: Number.isFinite(tenantId) ? tenantId : 1001,
    // 卖家端固定 TENANT —— 后端 SubjectTypeGuardFilter 按 /api/v1/ 前缀要求它
    subjectType: 'TENANT',
  })
}

const navigationLoading = document.getElementById('navigation-loading')
const navigationLabel = document.getElementById('navigation-loading-label')
const navigationRetry = document.getElementById('navigation-loading-retry')
navigationRetry?.addEventListener('click', () => window.location.reload())
router.beforeEach((to, from) => {
  if (to.path === from.path) return
  if (navigationLabel) navigationLabel.textContent = from.path === '/login' ? '正在进入工作台…' : '正在加载…'
  if (navigationLoading) navigationLoading.hidden = false
})
router.afterEach(() => { if (navigationLoading) navigationLoading.hidden = true })
router.onError(() => { if (navigationLoading) navigationLoading.hidden = true })
const app = createApp(App).use(createPinia()).use(router)

// Initial navigation resolves lazy route records and runs the auth guard. Mounting before this
// promise settles leaves `useRoute().meta` temporarily empty, so AppShell can paint the protected
// navigation for one frame on a cold anonymous visit. Do not replace this gate with a timer or a
// visual overlay: the protected component tree must not exist before the route is authoritative.
// Keep module evaluation synchronous: lazy route chunks import shared symbols from this entry
// chunk, so a top-level await here would make the route chunk and the entry wait on each other.
void router.isReady().then(() => {
  app.mount('#app')
  if (navigationLoading) navigationLoading.hidden = true
}).catch(() => {
  if (navigationLoading) navigationLoading.hidden = false
  if (navigationLabel) navigationLabel.textContent = '页面加载失败，请重试'
  if (navigationRetry) navigationRetry.hidden = false
})
